import * as React from 'react'
import { KeyboardEvent, MouseEvent, ReactElement, useContext, useEffect, useMemo } from 'react'
import { FontSizes, getTheme, IContextualMenuItem, IPivotItemProps, Pivot, PivotItem, ResizeGroup } from '@fluentui/react'
import { useApplicationStyles } from '../../pages/App'
import { ViewFileProps } from '../../pages/File/ViewFile'
import { useHistory } from 'react-router-dom'
import useFilePages, { FilePageProps } from '../../hooks/file/useFilePages'
import { useFileModules } from '../../hooks/module/useFileModule'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../hooks/useRoles'
import { ModuleType } from '../../context/GlobalContext'
import { useTranslation } from 'react-i18next'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import FileContext from '../../context/FileContext'

type PivotSizeProps = { cacheKey: number }

export default function ViewFilePivotItems({ file, fileId }: ViewFileProps): ReactElement {
    const { noteCount } = useContext(FileContext)
    const { attributeCategories } = useAttributes(AttributeViewType.View, [AttributeContextType.File], undefined, EntityAttributeType.File)
    const history = useHistory()
    const roles = useRoles()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const applicationStyles = useApplicationStyles()

    const { pages, activePage, getPageRoute } = useFilePages(fileId)
    const fileModules = useFileModules(file.current.data)

    const getMenuItemOnClick = (_?: MouseEvent | KeyboardEvent, item?: IContextualMenuItem) =>
        history.push(getPageRoute(pages.find(({ key }: FilePageProps) => key === item?.key)?.route))

    const registrationItems = useMemo(
        () =>
            [
                modules.has(ModuleType.TimeTracking) &&
                    roles.any(rolesTimes) && {
                        key: 'time',
                        text: translate('file.pivot.time.title'),
                        iconProps: { iconName: 'Timer' },
                        onClick: getMenuItemOnClick,
                    },
                modules.has(ModuleType.TravelExpenses) && {
                    key: 'travel',
                    text: translate('file.pivot.travel.title'),
                    iconProps: { iconName: 'Car' },
                    onClick: getMenuItemOnClick,
                },
                modules.has(ModuleType.Disbursements) && {
                    key: 'disbursement',
                    text: translate('file.pivot.disbursement.title'),
                    iconProps: { iconName: 'PaymentCard' },
                    onClick: getMenuItemOnClick,
                },
                modules.has(ModuleType.FixedPrices) && {
                    key: 'fixedPrice',
                    text: translate('file.pivot.fixedprice.title'),
                    iconProps: { iconName: 'GiftCard' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.Injury) && {
                    key: 'injury',
                    text: translate('file.pivot.injury.title'),
                    iconProps: { iconName: 'Hospital' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.FamilyLaw) && {
                    key: 'personlaw',
                    text: translate('file.pivot.personlaw.title'),
                    iconProps: { iconName: 'PeopleBlock' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.LegalAid) && {
                    key: 'legalAidCertificate',
                    text: translate('file.pivot.legalaidcertificate.title'),
                    iconProps: { iconName: 'Money' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.Docket) && {
                    key: 'docketProceeding',
                    text: translate('file.pivot.docketproceeding.title'),
                    iconProps: { iconName: 'CompanyDirectory' },
                    onClick: getMenuItemOnClick,
                },
            ].filter(Boolean),
        [file.current.loaded, modules, attributeCategories],
    )

    const attributeItems = useMemo(
        () =>
            modules.has(ModuleType.Attributes)
                ? attributeCategories?.map(category => {
                      const key = category || translate('file.pivot.attribute.title')
                      return {
                          itemKey: `attributes-${key.toLowerCase()}`,
                          itemIcon: 'BulletedTreeList',
                          headerText: key,
                      }
                  })
                : [],
        [modules, attributeCategories],
    )

    const pivotItems = useMemo(
        () =>
            [
                {
                    itemKey: 'party',
                    itemIcon: 'Family',
                    headerText: translate('file.pivot.party.title'),
                },
                {
                    itemKey: 'email',
                    itemIcon: 'Mail',
                    headerText: translate('file.pivot.email.title'),
                },
                {
                    itemKey: 'document',
                    itemIcon: 'DocLibrary',
                    headerText: translate('file.pivot.document.title'),
                },
                file.current.data.sharedDocumentRootURL && {
                    itemKey: 'shareddocument',
                    itemIcon: 'PageCheckedin',
                    headerText: translate('file.pivot.shareddocument.title'),
                    onClick: () => window.open(file.current.data.sharedDocumentRootURL, '_blank'),
                },
                modules.has(ModuleType.Tasks) &&
                    roles.any(rolesTasks) && {
                        itemKey: 'task',
                        itemIcon: 'TaskLogo',
                        headerText: translate('file.pivot.task.title'),
                    },
                modules.has(ModuleType.Workflow) && {
                    itemKey: 'workflow',
                    itemIcon: 'Dataflows',
                    headerText: translate('file.pivot.workflow.title'),
                },
                registrationItems.length > 0 && {
                    itemKey: 'registration',
                    itemIcon: 'BullseyeTargetEdit',
                    headerText: translate('file.pivot.registration.title'),
                    headerButtonProps: {
                        primary: true,
                        styles: { menuIcon: { color: 'inherit', marginRight: 0, fontSize: FontSizes.size10 } },
                        menuProps: {
                            items: registrationItems,
                        },
                    },
                },
                {
                    itemKey: 'note',
                    itemIcon: 'DietPlanNotebook',
                    headerText: translate('file.pivot.note.title'),
                    itemCount: noteCount || 0,
                },
                ...attributeItems,
                modules.has(ModuleType.Invoices) &&
                    roles.any([Role.Invoice]) && {
                        itemKey: 'monitoring',
                        itemIcon: 'AnalyticsReport',
                        headerText: translate('file.pivot.monitoring.title'),
                    },
                modules.has(ModuleType.AuditTrail) && {
                    itemKey: 'audittrail',
                    itemIcon: 'ProtectedDocument',
                    headerText: translate('file.pivot.audittrail.title'),
                },
            ].filter(Boolean),
        [file.current.loaded, noteCount, modules, roles, attributeItems],
    )

    useEffect(() => {
        fileModules.setCurrentFile(file.current.data)
    }, [file.current.loaded])

    return (
        <ResizeGroup
            className={applicationStyles.subMenu}
            data={{ cacheKey: pivotItems.length }}
            onReduceData={(data: PivotSizeProps): PivotSizeProps | undefined => (data.cacheKey > 0 ? { cacheKey: data.cacheKey - 1 } : undefined)}
            onGrowData={(data: PivotSizeProps): PivotSizeProps | undefined =>
                data.cacheKey < pivotItems.length ? { cacheKey: data.cacheKey + 1 } : undefined
            }
            onRenderData={(data: PivotSizeProps) => (
                <Pivot
                    linkSize={data.cacheKey === 1 ? 'large' : 'normal'}
                    linkFormat="tabs"
                    styles={{
                        count: { fontSize: FontSizes.size12 },
                        link: { ':after': { width: 0 }, ':active': { color: getTheme().palette.white } },
                    }}
                    selectedKey={activePage.pivotItemKey || activePage.key}
                    onLinkClick={item => {
                        const pageRoute = getPageRoute(pages.find(page => page.key === item.props.itemKey)?.route)
                        if (pageRoute) {
                            history.push(pageRoute)
                        } else {
                            item.props.onClick?.(undefined)
                        }
                    }}
                >
                    {pivotItems.map((item: IPivotItemProps, i: number) => (
                        <PivotItem
                            key={item.itemKey}
                            {...item}
                            headerButtonProps={{ title: item.headerText, ...item.headerButtonProps }}
                            headerText={i <= data.cacheKey && data.cacheKey >= 2 ? item.headerText : undefined}
                        />
                    ))}
                </Pivot>
            )}
        />
    )
}
